import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Campaign, CampaignListItem, Toast } from '@/types'
import { campaignService } from '@/services/campaigns'

const CAMPAIGNS_CACHE_KEY = 'sf_campaigns_cache'

function loadCachedCampaigns(): CampaignListItem[] {
  try {
    const raw = localStorage.getItem(CAMPAIGNS_CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return []
}

function saveCampaignsToCache(items: CampaignListItem[]) {
  try {
    localStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify(items))
  } catch { /* ignore */ }
}

export const useUiStore = defineStore('ui', () => {
  const currentCampaignId = ref<string | null>(null)
  const currentCampaign = ref<Campaign | null>(null)
  const loadingCampaign = ref(false)
  const showPublishModal = ref(false)
  const toasts = ref<Toast[]>([])
  const apiError = ref<{ title: string; message: string } | null>(null)
  const cachedCampaigns = ref<CampaignListItem[]>(loadCachedCampaigns())
  const campaignsLoaded = ref(false)

  const hasUnpublishedChanges = computed(() => currentCampaign.value?.unpublishedChanges?.hasChanges ?? false)
  const unpublishedCount = computed(() => currentCampaign.value?.unpublishedChanges?.count ?? 0)

  async function setCurrentCampaign(id: string | null) {
    if (id === currentCampaignId.value) return
    currentCampaignId.value = id
    if (!id) {
      currentCampaign.value = null
      return
    }
    loadingCampaign.value = true
    try {
      currentCampaign.value = await campaignService.getById(id)
    } catch {
      currentCampaign.value = null
    } finally {
      loadingCampaign.value = false
    }
  }

  async function refreshCurrentCampaign() {
    if (currentCampaignId.value) {
      try {
        currentCampaign.value = await campaignService.getById(currentCampaignId.value)
      } catch {
        // silently fail
      }
    }
  }

  function addToast(message: string, variant: Toast['variant'] = 'info', duration = 4000) {
    const id = Date.now().toString() + Math.random().toString(36).slice(2)
    const toast: Toast = { id, message, variant, duration }
    toasts.value.push(toast)
    setTimeout(() => {
      removeToast(id)
    }, duration)
  }

  function removeToast(id: string) {
    toasts.value = toasts.value.filter(t => t.id !== id)
  }

  function showApiError(title: string, message: string) {
    apiError.value = { title, message }
  }

  function dismissApiError() {
    apiError.value = null
  }

  async function fetchCampaignsList() {
    if (campaignsLoaded.value) return
    try {
      const result = await campaignService.getAll('Active', 1, 100)
      cachedCampaigns.value = result.items
      saveCampaignsToCache(result.items)
      campaignsLoaded.value = true
    } catch { /* silently fail, keep cached data */ }
  }

  async function invalidateCampaignsCache() {
    campaignsLoaded.value = false
    try {
      const result = await campaignService.getAll('Active', 1, 100)
      cachedCampaigns.value = result.items
      saveCampaignsToCache(result.items)
      campaignsLoaded.value = true
    } catch { /* silently fail */ }
  }

  return {
    currentCampaignId,
    currentCampaign,
    loadingCampaign,
    showPublishModal,
    toasts,
    apiError,
    cachedCampaigns,
    hasUnpublishedChanges,
    unpublishedCount,
    setCurrentCampaign,
    refreshCurrentCampaign,
    addToast,
    removeToast,
    showApiError,
    dismissApiError,
    fetchCampaignsList,
    invalidateCampaignsCache,
  }
})
